import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';

const RELATION_MISSING_CODES = new Set(['PGRST116', '42P01']);

const TRUE_STRINGS = new Set(['true', 't', '1', 'yes', 'y', 'ja', 'enabled', 'enable', 'active', 'aktiv', 'on']);
const FALSE_STRINGS = new Set(['false', 'f', '0', 'no', 'n', 'nein', 'disabled', 'disable', 'inactive', 'inaktiv', 'off']);

type AnySupabaseClient = SupabaseClient<any, any, any>;

type MembershipResult = {
  isOwner: boolean;
  featureFlag: boolean | null;
};

interface FeatureAccessDecision {
  isOwner: boolean;
  membershipFlag: boolean | null;
  overrideFlag: boolean | null;
}

export function isMissingUserFeatureAccessTable(error?: PostgrestError | null): boolean {
  if (!error) return false;
  if (RELATION_MISSING_CODES.has(error.code ?? '')) return true;
  const message = error.message?.toLowerCase() ?? '';
  return message.includes('user_feature_access');
}

export function interpretFeatureFlag(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (TRUE_STRINGS.has(normalized)) {
      return true;
    }
    if (FALSE_STRINGS.has(normalized)) {
      return false;
    }
  }

  return null;
}

async function fetchMembershipFeature(
  supabase: AnySupabaseClient,
  userId: string,
  organizationId: string,
  featureKey: string,
): Promise<MembershipResult> {
  const { data, error } = await supabase
    .from('organization_users')
    .select(`role, ${featureKey}`)
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .maybeSingle();

  if (error) throw error;

  const role = (data?.role ?? '').toString().toLowerCase();
  const isOwner = role === 'owner';
  let featureFlag: boolean | null = null;

  if (data && Object.prototype.hasOwnProperty.call(data, featureKey)) {
    const value = (data as Record<string, unknown>)[featureKey];
    const interpreted = interpretFeatureFlag(value);
    if (interpreted !== null) {
      featureFlag = interpreted;
    }
  }

  return { isOwner, featureFlag };
}

async function fetchUserFeatureOverride(
  supabase: AnySupabaseClient,
  userId: string,
  organizationId: string,
  featureKey: string,
): Promise<boolean | null> {
  const { data, error } = await supabase
    .from('user_feature_access')
    .select(featureKey)
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .maybeSingle();

  if (error) {
    if (isMissingUserFeatureAccessTable(error)) {
      return null;
    }
    throw error;
  }

  if (data && Object.prototype.hasOwnProperty.call(data, featureKey)) {
    const value = (data as Record<string, unknown>)[featureKey];
    const interpreted = interpretFeatureFlag(value);
    if (interpreted !== null) {
      return interpreted;
    }
  }

  return null;
}

export async function resolveUserFeatureAccess(
  supabase: AnySupabaseClient,
  userId: string,
  organizationId: string,
  featureKey: string,
): Promise<boolean> {
  if (!userId || !organizationId) {
    return false;
  }

  const membership = await fetchMembershipFeature(supabase, userId, organizationId, featureKey);

  const override = await fetchUserFeatureOverride(supabase, userId, organizationId, featureKey);
  return determineFeatureAccess({
    isOwner: membership.isOwner,
    membershipFlag: membership.featureFlag,
    overrideFlag: override,
  });
}

export function determineFeatureAccess({
  isOwner,
  membershipFlag,
  overrideFlag,
}: FeatureAccessDecision): boolean {
  if (isOwner) {
    return true;
  }

  if (overrideFlag !== null) {
    return overrideFlag;
  }

  if (membershipFlag !== null) {
    return membershipFlag;
  }

  return true;
}
